// Shared parser: audit result → module list.
// Extracted from AgentDetailPage so ClientDetailPage can reuse it.

export interface AuditModule {
  nodeId: string;
  name: string;
  status: string;
  score?: number;
  findings?: number;
  details: Record<string, any>;
  metadata: Record<string, any>;
  errorMessage?: string;
}

const CIS_NAMES: Record<string, string> = {
  'CIS 1': 'Enterprise Asset Inventory',
  'CIS 2': 'Software Asset Inventory',
  'CIS 3': 'Data Protection',
  'CIS 4': 'Secure Configuration',
  'CIS 5': 'Account Management',
  'CIS 6': 'Access Control Management',
  'CIS 7': 'Vulnerability Management',
  'CIS 8': 'Audit Log Management',
  'CIS 9': 'Email & Browser Protections',
  'CIS 10': 'Malware Defenses',
  'CIS 11': 'Data Recovery',
  'CIS 12': 'Network Infrastructure',
  'CIS 13': 'Network Monitoring & Defense',
};

export function parseAuditModules(auditResult: any): AuditModule[] {
  if (!auditResult) return [];

  const results = auditResult.results;
  if (!results) return [];

  const parsed = typeof results === 'string' ? JSON.parse(results) : results;

  if (parsed.children && Array.isArray(parsed.children)) {
    return parsed.children.map((child: any) => ({
      nodeId: child.nodeId || child.name,
      name: child.name || child.nodeId,
      status: child.status || 'unknown',
      details: child.details || {},
      metadata: child.metadata || {},
      errorMessage: child.errorMessage,
    }));
  }

  if (Array.isArray(parsed)) {
    return parsed;
  }

  if (auditResult.scoring?.module_scores) {
    const ms = typeof auditResult.scoring.module_scores === 'string'
      ? JSON.parse(auditResult.scoring.module_scores)
      : auditResult.scoring.module_scores;
    if (Object.keys(ms).length > 0) {
      return Object.entries(ms).map(([key, val]: [string, any]) => ({
        nodeId: key,
        name: key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
        status: val.status || 'unknown',
        score: val.score,
        findings: val.findings,
        details: val,
        metadata: {},
      }));
    }
  }

  if (auditResult.scoring?.cis_scores) {
    const cis = typeof auditResult.scoring.cis_scores === 'string'
      ? JSON.parse(auditResult.scoring.cis_scores)
      : auditResult.scoring.cis_scores;
    if (Object.keys(cis).length > 0) {
      return Object.entries(cis).map(([key, val]: [string, any]) => ({
        nodeId: key,
        name: `${key} — ${CIS_NAMES[key] || key}`,
        status: val.status || 'unknown',
        score: val.score,
        findings: val.findings,
        details: { coverage: val.coverage, ...val },
        metadata: { source: 'cis_scores' },
      }));
    }
  }

  return [];
}
